import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ArrowLeft, Box, Check, ExternalLink, FilePlus2, Search, SlidersHorizontal, Tag } from "lucide-react";
import { ALL_TOPICS, BANK_LEVELS, BANK_LICENSES, BANK_SUBJECTS, BANK_TOPICS, type BankSubject } from "@jange/shared";
import { api, can, type BankItem } from "../lib/api";
import { useStudio } from "../lib/context";
import { fmtBytes } from "../lib/upload";
import { Drawer, Empty, ErrorBox, errorMessage, Field, Loading } from "./ui";

const Model3D = lazy(() => import("@jange/lesson-player/model3d"));
const SUBJECTS = Object.entries(BANK_SUBJECTS) as [BankSubject, string][];

/**
 * Banque 3D : chercher (matière, niveau, thème, mot), prévisualiser en 3D, puis ajouter à une leçon.
 * Sert de page (`mode="page"`) et de sélecteur dans l'éditeur de leçon (`mode="pick"`).
 */
export function BankBrowser({ mode, onPick }: { mode: "page" | "pick"; onPick?: (m: BankItem) => void }) {
  const [subject, setSubject] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [topic, setTopic] = useState<string>("");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<BankItem | null>(null);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const facets = useQuery({ queryKey: ["bank-facets"], queryFn: api.bankFacets, staleTime: 60_000 });
  const list = useInfiniteQuery({
    queryKey: ["bank", subject, level, topic, debounced],
    queryFn: ({ pageParam }) => api.bank({ subject, level, topic, q: debounced, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const f = facets.data;
  const topics = subject && subject !== "none" ? Object.entries(BANK_TOPICS[subject as BankSubject] ?? {}) : [];
  const filtered = !!(subject || level || topic || debounced);

  if (mode === "pick" && selected) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        <button type="button" className="btn sm subtle" style={{ alignSelf: "flex-start" }} onClick={() => setSelected(null)}><ArrowLeft size={15} />Retour à la banque</button>
        <BankDetail m={selected} mode="pick" onPick={onPick} />
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <div className="input-icon" style={{ flex: "1 1 260px", maxWidth: 380 }}>
          <Search size={15} />
          <input className="input" placeholder="Chercher : cœur, ADN, poumons, volcan…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Chercher un modèle" autoFocus={mode === "pick"} />
        </div>
        <div className="seg" role="group" aria-label="Matière">
          <button type="button" aria-pressed={!subject} onClick={() => { setSubject(""); setTopic(""); }}>Toutes {f && <span className="n">{f.total}</span>}</button>
          {SUBJECTS.filter(([k]) => (f?.subjects[k] ?? 0) > 0).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={subject === k} onClick={() => { setSubject(k); setTopic(""); }}>{label} <span className="n">{f?.subjects[k]}</span></button>
          ))}
          {(f?.unclassified ?? 0) > 0 && <button type="button" aria-pressed={subject === "none"} onClick={() => { setSubject("none"); setTopic(""); }}>Non classés <span className="n">{f?.unclassified}</span></button>}
        </div>
      </div>
      <div className="row wrap" style={{ gap: 6 }}>
        <span className="tiny muted strong" style={{ marginRight: 2 }}>Niveau</span>
        {BANK_LEVELS.map((l) => (
          <button key={l} type="button" className="chip" aria-pressed={level === l} disabled={!f?.levels[l] && level !== l} onClick={() => setLevel(level === l ? "" : l)}>{l}</button>
        ))}
        {topics.length > 0 && <>
          <span className="tiny muted strong" style={{ margin: "0 2px 0 10px" }}>Thème</span>
          {topics.filter(([k]) => (f?.topics[k] ?? 0) > 0 || topic === k).map(([k, label]) => (
            <button key={k} type="button" className="chip" aria-pressed={topic === k} onClick={() => setTopic(topic === k ? "" : k)}>{label}</button>
          ))}
        </>}
      </div>

      {list.isLoading && <Loading lines={6} />}
      {list.error && <ErrorBox error={list.error} onRetry={() => list.refetch()} />}
      {list.data && items.length === 0 && (
        <div className="card"><Empty icon={<Box size={20} />} title={filtered ? "Aucun modèle pour cette recherche." : "La banque est encore vide."}>
          {filtered ? "Essayez un autre mot ou retirez un filtre." : "Les modèles arrivent avec l'import en série (Administration → État du service), ou s'ajoutent depuis la médiathèque."}
        </Empty></div>
      )}
      <div className="bank-grid">
        {items.map((m) => (
          <button key={m.id} type="button" className="bank-card appear" onClick={() => setSelected(m)} aria-label={`Aperçu : ${m.title}`}>
            <Thumb m={m} />
            <span className="bank-card-body">
              <b>{m.title}</b>
              <span className="row wrap" style={{ gap: 4 }}>
                {m.levels.slice(0, 4).map((l) => <span key={l} className="badge plain b-DRAFT">{l}</span>)}
                {m.topics.slice(0, 1).map((t) => <span key={t} className="badge plain b-INFO">{ALL_TOPICS[t] ?? t}</span>)}
              </span>
            </span>
          </button>
        ))}
      </div>
      {list.hasNextPage && <div style={{ textAlign: "center" }}><button className="btn ghost" disabled={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>Afficher plus</button></div>}

      {mode === "page" && selected && (
        <DetailDrawer m={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function Thumb({ m }: { m: BankItem }) {
  return m.thumbUrl
    ? <span className="bank-thumb"><img src={m.thumbUrl} alt="" loading="lazy" /></span>
    : <span className="bank-thumb ph" aria-hidden><Box size={30} strokeWidth={1.5} /><span className="tiny">Aperçu 3D</span></span>;
}

function DetailDrawer({ m, onClose }: { m: BankItem; onClose: () => void }) {
  return (
    <Drawer title={m.title} wide onClose={onClose} subtitle={<>{m.subjects.map((s) => BANK_SUBJECTS[s as BankSubject] ?? s).join(" · ") || "Non classé"}{m.levels.length ? ` · ${m.levels.join(", ")}` : ""}</>}>
      <BankDetail m={m} mode="page" onDone={onClose} />
    </Drawer>
  );
}

/** Aperçu 3D, informations, licence ; ajouter à une leçon (ou choisir, dans l'éditeur) ; classer. */
function BankDetail({ m: initial, mode, onPick, onDone }: { m: BankItem; mode: "page" | "pick"; onPick?: (m: BankItem) => void; onDone?: () => void }) {
  const { teacher } = useStudio();
  const qc = useQueryClient();
  const fresh = useQuery({ queryKey: ["bank-item", initial.id], queryFn: () => api.bankItem(initial.id), initialData: initial, staleTime: 30_000 });
  const m = fresh.data;
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const thumbTried = useRef(false);

  // Vignette : faite par ce navigateur au premier aperçu, puis partagée par toute l'équipe.
  const onReady = useCallback((snap: () => string | null) => {
    if (m.thumbUrl || thumbTried.current) return;
    thumbTried.current = true;
    setTimeout(async () => {
      const raw = snap();
      if (!raw) return;
      const image = await downscale(raw, 480);
      try { await api.bankThumbnail(m.id, image); void qc.invalidateQueries({ queryKey: ["bank"] }); void qc.invalidateQueries({ queryKey: ["bank-item", m.id] }); }
      catch { /* sans vignette, la carte garde son pictogramme */ }
    }, 900);
  }, [m.id, m.thumbUrl, qc]);

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="bank-preview">
        <Suspense fallback={<div className="muted small" style={{ padding: 20 }}>Chargement de la 3D…</div>}>
          <Model3D src={m.url} poster={m.thumbUrl} sizeBytes={m.sizeBytes} hotspots={[]} lang="fr" autoLoad bare height={360} onReady={onReady} credit={m.credit} />
        </Suspense>
      </div>
      <div className="row wrap" style={{ gap: 8 }}>
        {mode === "pick"
          ? <button className="btn" onClick={() => onPick?.(m)}><Check size={16} />Utiliser ce modèle</button>
          : can.author(teacher) && <button className="btn" onClick={() => setAdding(!adding)} aria-expanded={adding}><FilePlus2 size={16} />Ajouter à une leçon</button>}
        {can.author(teacher) && <button className="btn ghost" onClick={() => setEditing(!editing)} aria-expanded={editing}><Tag size={15} />Classer</button>}
        <span className="spacer" />
        <span className="small muted">{fmtBytes(m.sizeBytes)}{m.triangles ? ` · ${m.triangles.toLocaleString("fr-FR")} triangles` : ""}</span>
      </div>
      {adding && <AddToLesson m={m} onDone={onDone} />}
      {editing && <Classify m={m} onSaved={() => setEditing(false)} />}
      {m.description && <p className="dim">{m.description}</p>}
      <div className="kv">
        <div><span>Matières</span><b>{m.subjects.map((s) => BANK_SUBJECTS[s as BankSubject] ?? s).join(", ") || "—"}</b></div>
        <div><span>Niveaux</span><b>{m.levels.join(", ") || "—"}</b></div>
        <div><span>Thèmes</span><b>{m.topics.map((t) => ALL_TOPICS[t] ?? t).join(", ") || "—"}</b></div>
        <div><span>Licence</span><b>{BANK_LICENSES[m.license as keyof typeof BANK_LICENSES] ?? m.license}</b></div>
        {m.credit && <div style={{ gridColumn: "1 / -1" }}><span>Crédit affiché aux élèves sous le modèle</span><b style={{ fontWeight: 600 }}>{m.credit}</b></div>}
        {m.sourceUrl && <div style={{ gridColumn: "1 / -1" }}><span>Source</span><a href={m.sourceUrl} target="_blank" rel="noreferrer" className="row" style={{ gap: 4, display: "inline-flex" }}>{new URL(m.sourceUrl).hostname}<ExternalLink size={12} /></a></div>}
      </div>
    </div>
  );
}

/** Choisir la leçon : la notion s'ouvre avec le modèle déjà posé sur une nouvelle carte. */
function AddToLesson({ m, onDone }: { m: BankItem; onDone?: () => void }) {
  const { countryId, languages } = useStudio();
  const nav = useNavigate();
  const tree = useQuery({ queryKey: ["curricula", countryId], queryFn: () => api.curricula(countryId) });
  const [q, setQ] = useState("");
  const [lang, setLang] = useState(languages[0]?.code ?? "fr");
  const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const notions = useMemo(() => (tree.data ?? []).flatMap((cv) => cv.chapters.flatMap((ch) => ch.notions.map((n) => ({ ...n, chapter: ch, cv })))), [tree.data]);
  const f = fold(q.trim());
  const shown = notions.filter((n) => !f || fold(`${n.title} ${n.chapter.title}`).includes(f)).slice(0, 8);
  return (
    <section className="card flat stack appear" style={{ background: "var(--surface-2)", gap: 10 }} aria-label="Ajouter à une leçon">
      <div className="row wrap" style={{ gap: 8 }}>
        <div className="input-icon" style={{ flex: "1 1 220px" }}><Search size={15} /><input className="input" autoFocus placeholder="Chercher la notion…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Chercher la notion" /></div>
        {languages.length > 1 && (
          <div className="seg" role="group" aria-label="Langue de la leçon">
            {languages.map((l) => <button key={l.code} type="button" aria-pressed={lang === l.code} onClick={() => setLang(l.code)}>{l.nativeName ?? l.name}</button>)}
          </div>
        )}
      </div>
      {tree.isLoading && <Loading lines={3} />}
      {shown.map((n) => (
        <button key={n.id} type="button" className="list-row" onClick={() => { onDone?.(); nav(`/notions/${n.id}?lang=${lang}&insertModel=${m.id}`); }}>
          <span style={{ flex: 1, minWidth: 0 }}><b style={{ display: "block" }}>{n.title}</b><span className="tiny muted">{n.cv.subject} · {n.cv.grade} · Ch. {n.chapter.ordinal} {n.chapter.title}</span></span>
          <span className="btn sm ghost"><FilePlus2 size={14} />Ajouter</span>
        </button>
      ))}
      {tree.data && shown.length === 0 && <p className="small muted">Aucune notion ne correspond.</p>}
      <p className="tiny muted">Le modèle est posé sur une nouvelle carte, à la fin de la leçon : déplacez-la, puis enregistrez le brouillon.</p>
    </section>
  );
}

function Classify({ m, onSaved }: { m: BankItem; onSaved: () => void }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const [title, setTitle] = useState(m.title);
  const [subjects, setSubjects] = useState<string[]>(m.subjects);
  const [levels, setLevels] = useState<string[]>(m.levels);
  const [topics, setTopics] = useState<string[]>(m.topics);
  const [keywords, setKeywords] = useState(m.keywords.join(", "));
  const [description, setDescription] = useState(m.description ?? "");
  const toggle = (xs: string[], x: string) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);
  const save = useMutation({
    mutationFn: () => api.bankUpdate(m.id, { title: title.trim(), subjects, levels, topics: topics.filter((t) => subjects.some((s) => t in (BANK_TOPICS[s as BankSubject] ?? {}))), keywords: keywords.split(",").map((k) => k.trim()).filter((k) => k.length >= 2).slice(0, 20), description: description.trim() || null }),
    onSuccess: () => { toast("Classement enregistré"); void qc.invalidateQueries({ queryKey: ["bank"] }); void qc.invalidateQueries({ queryKey: ["bank-facets"] }); void qc.invalidateQueries({ queryKey: ["bank-item", m.id] }); onSaved(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  return (
    <form className="card flat stack appear" style={{ background: "var(--surface-2)", gap: 12 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }} aria-label="Classer le modèle">
      <h3 className="row" style={{ gap: 8 }}><SlidersHorizontal size={15} />Classement</h3>
      <Field label="Titre"><input className="input" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} /></Field>
      <div className="field"><span>Matières</span><div className="row wrap" style={{ gap: 6 }}>{SUBJECTS.map(([k, l]) => <button key={k} type="button" className="chip" aria-pressed={subjects.includes(k)} onClick={() => setSubjects(toggle(subjects, k))}>{l}</button>)}</div></div>
      <div className="field"><span>Niveaux</span><div className="row wrap" style={{ gap: 6 }}>{BANK_LEVELS.map((l) => <button key={l} type="button" className="chip" aria-pressed={levels.includes(l)} onClick={() => setLevels(toggle(levels, l))}>{l}</button>)}</div></div>
      {subjects.length > 0 && (
        <div className="field"><span>Thèmes</span><div className="row wrap" style={{ gap: 6 }}>
          {subjects.flatMap((s) => Object.entries(BANK_TOPICS[s as BankSubject] ?? {})).map(([k, l]) => <button key={k} type="button" className="chip" aria-pressed={topics.includes(k)} onClick={() => setTopics(toggle(topics, k))}>{l}</button>)}
        </div></div>
      )}
      <Field label="Mots-clés (séparés par des virgules)" hint="Ce que l'enseignant tapera pour le trouver : « cœur, circulation, ventricule »."><input className="input" value={keywords} onChange={(e) => setKeywords(e.target.value)} /></Field>
      <Field label="Description"><textarea className="textarea" maxLength={600} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <div className="row"><span className="spacer" /><button className="btn" disabled={save.isPending || title.trim().length < 2}>Enregistrer le classement</button></div>
    </form>
  );
}

/** Réduit la photo de la vue (WebP, 480 px de large) avant l'envoi. */
async function downscale(dataUrl: string, width: number): Promise<string> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const ratio = Math.min(1, width / img.width);
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * ratio); c.height = Math.round(img.height * ratio);
  c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
  const out = c.toDataURL("image/webp", 0.8);
  return out.startsWith("data:image/webp") ? out : c.toDataURL("image/jpeg", 0.82);
}
